import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Loader2, ImageOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useApi, errMsg } from "@/lib/api";
import { photoSrc } from "@/components/home/Doctor";

const Photo = ({ d, setD }) => {
  const api = useApi();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { setD(await api("post", "/admin/doctor/photo", fd)); toast.success("Photo updated"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    input.current.value = "";
  };
  const remove = async () => {
    try { setD(await api("delete", "/admin/doctor/photo")); toast.success("Photo removed"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-doctor-photo">
      <p className="font-semibold text-slate-800">Profile photo</p>
      <img src={photoSrc(d)} alt="" className="mt-3 aspect-[4/5] w-full rounded-2xl object-cover" data-testid="admin-doctor-photo-preview" />
      {!d.photo_url && <p className="mt-2 text-xs text-slate-500">Showing the default clinic photo.</p>}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="admin-doctor-photo-input" />
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => input.current.click()} disabled={busy} data-testid="admin-doctor-photo-upload"
          className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload new photo</button>
        {d.photo_url && <button onClick={remove} data-testid="admin-doctor-photo-remove" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700"><ImageOff className="h-4 w-4" />Use default</button>}
      </div>
      <p className="mt-2 text-xs text-slate-400">JPG, PNG or WEBP, up to 5 MB. A portrait (taller than wide) looks best.</p>
    </div>
  );
};

const Quals = ({ list, onChange }) => (
  <div>
    <Label>Qualification cards (up to 8)</Label>
    <div className="mt-2 space-y-2">
      {list.map((q, i) => (
        <div key={i} className="flex gap-2" data-testid="admin-doctor-qual-row">
          <Input className="w-40" placeholder="e.g. BDS 2012" value={q.title} onChange={(e) => onChange(list.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} data-testid={`admin-doctor-qual-title-${i}`} />
          <Input placeholder="e.g. Mumbai, 1st class" value={q.detail} onChange={(e) => onChange(list.map((x, k) => (k === i ? { ...x, detail: e.target.value } : x)))} data-testid={`admin-doctor-qual-detail-${i}`} />
          <button onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label="Remove" data-testid={`admin-doctor-qual-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></button>
        </div>
      ))}
    </div>
    {list.length < 8 && (
      <button onClick={() => onChange([...list, { title: "", detail: "" }])} data-testid="admin-doctor-qual-add"
        className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-teal-700"><Plus className="h-4 w-4" />Add card</button>
    )}
  </div>
);

export const AdminDoctor = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api("get", "/doctor").then((r) => { setD(r); setF(r); }).catch(() => { loaded.current = false; });
  }, [api]);
  if (!f) return <p className="text-slate-500">Loading...</p>;

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: f.name, credentials: f.credentials, heading: f.heading, bio: f.bio, qualifications: f.qualifications.filter((q) => q.title.trim()) };
      const r = await api("put", "/admin/doctor", body);
      setD(r); setF(r); toast.success("Doctor profile saved");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="admin-doctor">
      <Photo d={d} setD={(r) => { setD(r); setF((x) => ({ ...x, photo_url: r.photo_url })); }} />
      <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Doctor's name</Label><Input className="mt-2" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-doctor-name" /></div>
          <div><Label>Short credentials (shown on photo)</Label><Input className="mt-2" value={f.credentials} onChange={(e) => setF({ ...f, credentials: e.target.value })} data-testid="admin-doctor-credentials" /></div>
        </div>
        <div><Label>Heading</Label><Input className="mt-2" value={f.heading} onChange={(e) => setF({ ...f, heading: e.target.value })} data-testid="admin-doctor-heading" /></div>
        <div><Label>Bio (leave an empty line between paragraphs)</Label><Textarea className="mt-2" rows={8} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="admin-doctor-bio" /></div>
        <Quals list={f.qualifications} onChange={(qualifications) => setF({ ...f, qualifications })} />
        <button onClick={save} disabled={saving} data-testid="admin-doctor-save"
          className="w-full rounded-full bg-[#ff6b6b] py-3 font-bold text-white disabled:opacity-50 sm:w-auto sm:px-8">{saving ? "Saving..." : "Save profile"}</button>
      </div>
    </div>
  );
};
